import React, { useState, useEffect, useRef } from 'react'

import * as config from 'styles/tailwind.config'
import NumberInput from '../numberInput'
import { Clear } from 'images/icons'
import DateInput from '../dateInput'

interface IProps {
  type?: string
  label?: string
  value?: string | number | Date | null
  min?: number
  max?: number
  name: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  underline?: boolean
  leftAlign?: boolean
  allowClear?: boolean
  hideUnit?: boolean
  rightAlign?: boolean
  icon?: any
  unit?: string
  onChange?: (value: string | number) => void
  onBlur?: (value: string | number) => void
  onDateChange?: (value: Date | Date[]) => void
}

const Input: React.FC<IProps> = (props) => {
  const {
    type,
    label,
    value,
    name,
    placeholder,
    error,
    required,
    disabled,
    min,
    max,
    underline,
    leftAlign,
    icon,
    unit,
    allowClear,
    hideUnit,
    rightAlign,
  } = props

  const inputEl = useRef<HTMLInputElement>(null)
  const [state] = useState<string | number | Date | undefined>(value !== null ? value : undefined)

  // Clear input if passed value is an empty string
  useEffect(() => {
    const { onChange } = props

    if (inputEl.current && inputEl.current.value) {
      if (value === '') {
        inputEl.current.value = ''

        if (onChange) {
          onChange('')
        }
      }
    }
  }, [value, props])

  const handleDateChange = (value: Date | Date[]) => {
    const { onDateChange } = props

    if (onDateChange) {
      onDateChange(value)
    }
  }

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const { onBlur } = props

    if (onBlur) {
      onBlur(value)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const { onChange } = props

    if (onChange) {
      onChange(value)
    }
  }

  const clearInput = () => {
    const { onChange } = props

    if (inputEl.current && inputEl.current.value) {
      inputEl.current.value = ''

      if (onChange) {
        onChange('')
      }
    }
  }

  const isActive = () => state !== ''

  if (underline) {
    return (
      <div className="w-full">
        {type === 'number' && unit ? (
          <NumberInput
            className={`w-full block no-arrows border-b-1 ${
              isActive() ? 'border-black' : 'border-gray-200'
            } ${error ? 'border-red' : ''} pb-10 text-black text-13 font-400 tracking-0.1 ${
              leftAlign ? 'text-left' : 'text-right'
            }`}
            unit={unit}
            name={name}
            defaultValue={value !== null && value !== undefined ? String(value) : undefined}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            hideUnit={hideUnit}
            rightAlign={rightAlign}
            min={min}
            max={max}
          />
        ) : (
          <input
            ref={inputEl}
            className={`w-full block no-arrows border-b-1 ${
              isActive() ? 'border-black' : 'border-gray-200'
            } ${error ? 'border-red' : ''} pb-10 text-black text-13 font-400 tracking-0.1 ${
              leftAlign ? 'text-left' : 'text-right'
            }`}
            id={name}
            name={name}
            type={'text'}
            value={value !== null && value !== undefined ? String(value) : undefined}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            min={min}
            max={max}
          />
        )}
      </div>
    )
  }

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={name} className={`label ${error && 'text-red'}`}>
          {label} {required && '*'}
        </label>
      )}
      <div className={icon ? 'w-full relative' : 'w-full'}>
        {icon && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              transform: 'translate(0, -50%)',
              left: config.theme.spacing['19.5'],
            }}
          >
            {icon}
          </div>
        )}
        {allowClear && state && String(state).length && (
          <button
            style={{
              position: 'absolute',
              top: '50%',
              transform: 'translate(0, -50%)',
              right: config.theme.spacing['19.5'],
            }}
            onClick={clearInput}
          >
            {<Clear />}
          </button>
        )}
        {unit ? (
          <div
            className={`w-full flex items-center border-1 ${
              error ? 'border-red' : 'border-gray-200'
            }`}
          >
            <NumberInput
              className={`w-full no-arrows pl-19.5 pt-16.5 pb-15.5 ${
                icon ? 'pl-50' : ''
              } text-black text-15 font-500 tracking-0.12 ${
                leftAlign ? 'text-left' : 'text-right'
              }`}
              unit={unit}
              name={name}
              defaultValue={value !== null && value !== undefined ? String(value) : undefined}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={placeholder}
              hideUnit={hideUnit}
              rightAlign={rightAlign}
              min={min}
              max={max}
            />
            <div className="w-6/12 flex">
              <span className="pl-3 pr-19.5 pt-16.5 pb-15.5 text-black text-15 font-500 tracking-0.12">
                {unit}
              </span>
            </div>
          </div>
        ) : type === 'date' ? (
          <DateInput
            name={name}
            value={
              value !== null &&
              value !== undefined &&
              typeof value !== 'number' &&
              typeof value !== 'string'
                ? value
                : undefined
            }
            onChange={handleDateChange}
          />
        ) : (
          <input
            ref={inputEl}
            className={`w-full block no-arrows border-1 ${
              error ? 'border-red' : 'border-gray-200'
            } px-19.5 pt-16.5 pb-15.5 ${icon ? 'pl-50' : ''} ${
              type === 'date' && 'pr-0'
            } text-15 font-500 tracking-0.12 ${leftAlign ? 'text-left' : 'text-right'} ${
              disabled ? 'cursor-not-allowed text-gray-300' : 'text-black'
            }`}
            id={name}
            name={name}
            type={type || 'text'}
            value={value !== null && value !== undefined ? String(value) : undefined}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            min={min}
            max={max}
          />
        )}
      </div>
    </div>
  )
}

export default Input
